// activity-modal-data.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface ActivityData {
  id?: number;
  created_at?: string;
  updated_at?: string;
  company_id?: number;
  title?: string;
  task_type_id?: any;
  priority?: any;
  lead_id?: number;
  assigned_to_id?: number;
  created_by_id?: number;
  meeting_owner_id?: any;
  dueDate?: Date | string;
  startDate?: Date | string;
  completionDate?: any;
  actualHours?: any;
  notes?: string;
  tags?: any[];
  last_updated_by_id?: number;
  status?: string;
  type_id?: number;
  endDate?: Date | string;
  completed?: boolean;
  isActivity?: boolean;
  active?: boolean;
  campus_id?: number;
  allDay?: boolean;
  isAutomation?: boolean;
  googleCalendarId?: string;
  appointment_id?: any;
  meetingLink?: string;
  meetingName?: string;
  meetingCode?: string;
  lead?: any;
  assignedTo?: any;
  createdBy?: any;
  lastUpdatedBy?: any;
  type?: any;
  campus?: any;
  task_attendees?: any[];
  appointment?: any;
  start?: Date;
  end?: Date;
  listView?: boolean;
  hourSegments?: number;
  activityTypes?: any[];
  detailed?: boolean;
  editMode?: boolean;
  isModal?: boolean;
  isExternal?: boolean;
  leadView?: boolean;
  attendanceStatus?: string;
  outcome_id?: number;
  isVirtual?: boolean;
  defaultLead: any;
  newTask:any;
  oldTask:any;
  newAppointment: boolean;
  originalTask: any;
  // Add any other properties you need
}

export type ActivityAction = 'create' | 'update' | 'delete' | 'bulk-update';

export interface ActivityEvent {
  action: ActivityAction;
  data: ActivityData;
}

@Injectable({
  providedIn: 'root'
})
export class ActivityModalDataService {
  // Private subjects to store data
  private _activityData = new BehaviorSubject<ActivityData | null>(null);
  private _leadData = new BehaviorSubject<any>(null);
  private _isLoading = new BehaviorSubject<boolean>(false);
  
  // Public observables for components to subscribe to
  public readonly activityData$: Observable<ActivityData | null> = this._activityData.asObservable();
  public readonly leadData$: Observable<any> = this._leadData.asObservable();
  public readonly isLoading$: Observable<boolean> = this._isLoading.asObservable();

  private _activityEvents = new Subject<ActivityEvent>();
  activityEvents$ = this._activityEvents.asObservable();

  private _leadUpdatesEvents = new Subject<any>();
  public readonly leadUpdatesEvents$ = this._leadUpdatesEvents.asObservable();

   // Add navigation subject
   private leadNavigationSubject = new Subject<number>();
   leadNavigationEvents$ = this.leadNavigationSubject.asObservable();

   private activityNavigationSubject = new Subject<number>();
   activityNavigationEvents$ = this.activityNavigationSubject.asObservable();  

  constructor() {}

  emitEvent(action: ActivityAction, data: ActivityData) {
    this._activityEvents.next({ action, data });
  }

  emitLeadUpdate(leadData: any): void {
    this._leadUpdatesEvents.next(leadData);
  }
  /**
   * Set activity data with proper date handling
   */
  setActivityData(data: any): void {

    if (!data) {
      this._activityData.next(null);
      return;
    }
  
    // The key insight: ensure start/end are always Date objects, never strings
    const processedData = {
      ...data,
      start: this.ensureDate(data.start),
      end: this.ensureDate(data.end),
      startDate: this.ensureDate(data.startDate),
      endDate: this.ensureDate(data.endDate),
    };

  
    this._activityData.next(processedData);
  }
  
  // Helper method to ensure we have proper Date objects
  private ensureDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value; // Already a Date, return as-is
    
    // Convert string/number to Date
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
 * Get the current lead ID from either lead data or activity data
 */
getCurrentLeadId(): string | null {
  const leadData = this._leadData.value;
  if (leadData?.id) {
    return leadData.id.toString();
  }
  
  const activityData = this._activityData.value;
  if (activityData?.lead?.id) {
    return activityData.lead.id.toString();
  }
  
  if (activityData?.defaultLead) {
    return activityData.defaultLead.toString();
  }
  
  return null;
}

/**
 * Check if we have lead data or a lead reference
 */
hasLeadReference(): boolean {
  return this.hasLeadData() || !!this.getCurrentLeadId();
}


  /**
   * Set lead data
   */
  setLeadData(data: any): void {
    const clonedData = data ? { ...data } : null;
    this._leadData.next(clonedData);
  }

  /**
   * Set loading state
   */
  setLoading(loading: boolean): void {
    this._isLoading.next(loading);
  }

  /**
   * Get current activity data (returns a fresh copy)
   */
  getCurrentActivityData(): any {
    const current = this._activityData.value;
    return current; // Return as-is, no cloning
}

  /**
   * Get current lead data
   */
  getCurrentLeadData(): any {
    const current = this._leadData.value;
    return current ? { ...current } : null;
  }

  /**
   * Update a specific field in the activity data
   */
  updateActivityField(field: keyof ActivityData, value: any): void {
    const current = this._activityData.value;
    if (!current) return;

    const updated :any= { ...current };
    
    // Handle Date fields specially
    if (field === 'start' || field === 'end' || field === 'startDate' || field === 'endDate' || field === 'dueDate') {
      updated[field] = this.cloneDate(value);
    } else {
      updated[field] = value;
    }

    this._activityData.next(updated);
  }

  /**
 * Update activity data with new lead reference
 */
updateActivityLead(leadId: string | null): void {
  const current = this._activityData.value;
  if (!current) return;

  const updated = { 
    ...current,
    defaultLead: leadId ? parseInt(leadId) : null
  };

  this._activityData.next(updated);
}

  /**
   * Update multiple fields at once
   */
  updateActivityFields(updates: Partial<ActivityData>): void {
    const current = this._activityData.value;
    if (!current) return;

    const updated = { ...current };
    
    // Apply all updates
    Object.keys(updates).forEach((key: any) => {
      const field:any = key as keyof ActivityData;
      const value:any = updates[field];
      
      // Handle Date fields specially
      if (field === 'start' || field === 'end' || field === 'startDate' || field === 'endDate' || field === 'dueDate') {
        updated[field] = this.cloneDate(value);
      } else {
        updated[field] = value;
      }
    });

    this._activityData.next(updated);
  }

  /**
   * Clear all data (useful for cleanup)
   */
  clear(): void {
    this._activityData.next(null);
    this._leadData.next(null);
    this._isLoading.next(false);
  }

  /**
   * Check if service has activity data
   */
  hasActivityData(): boolean {
    return this._activityData.value !== null;
  }

  /**
   * Check if service has lead data
   */
  hasLeadData(): boolean {
    return this._leadData.value !== null;
  }

  /**
   * Helper method to properly clone Date objects
   */
  private cloneDate(date: any): Date | null {
    if (!date) return null;
    
    if (date instanceof Date) {
      return new Date(date.getTime());
    }
    
    if (typeof date === 'string') {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    
    return null;
  }

  /**
   * Debug method to log current state
   */
  debugCurrentState(): void {
    const activityData = this._activityData.value;
    const leadData = this._leadData.value;
  }
 
   // Add method to emit navigation events
   emitLeadNavigation(leadId: number): void {
    this.leadNavigationSubject.next(leadId);
  }

  emitActivityNavigation(activityId: number): void {
    this.activityNavigationSubject.next(activityId);
  }
}